// generated by the plop

import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { PROGRESS_STATUSES } from 'symbols';
import { TalonsService } from '../../services/talons.service';
import { TalonModel, TALONS_LOCAL_STORAGE_KEY } from '../../symbols';
import { TalonAccessState } from '../talon-access/talon-access.state';
import {
  CreatedTalonByWS,
  CreateTalon,
  CreateTalonFail,
  CreateTalonSuccess,
  CreateTalonTemplate,
  DeleteLocalTalon,
  DeleteTalonTemplate,
  GetTalonById,
  GetTalonByIdFail,
  GetTalonByIdSuccess,
  GetTalonTemplateList,
  SetCurrentTalonId,
  UpdatedTalonByWS,
  UpdateLocalTalon,
  UpdateTalon,
  UpdateTalonFail,
  UpdateTalonSuccess,
  UpdateTalonTemplate,
  UpdateTalonTemplateFail,
  UpdateTalonTemplateSuccess
} from './talon-manage.actions';
import { TALON_MANAGE_STATE_DEFAULT, TalonManageStateModel } from './talon-manage.model';


@State({ name: 'TalonManage', defaults: TALON_MANAGE_STATE_DEFAULT })
@Injectable()
export class TalonManageState implements NgxsOnInit {

  constructor(private talonsService: TalonsService, private store: Store) {
  }


  @Selector()
  static realVersionTalons(state: TalonManageStateModel): TalonManageStateModel['realVersionTalons'] {
    return state.realVersionTalons;
  }

  @Selector()
  static currentTalon(state: TalonManageStateModel): TalonModel {
    return state.talons?.[state.currentTalonId];
  }

  @Selector()
  static loadTalonStatus(state: TalonManageStateModel): PROGRESS_STATUSES {
    return state.loadTalonStatus;
  }

  @Selector()
  static talonTemplateList(state: TalonManageStateModel): TalonManageStateModel['templateList'] {
    return state.templateList;
  }

  @Selector()
  static loadTemplateListStatus(state: TalonManageStateModel): PROGRESS_STATUSES {
    return state.loadTemplateListStatus;
  }

  ngxsOnInit(ctx: StateContext<TalonManageStateModel>) {

    const localStorageTalons = localStorage.getItem(TALONS_LOCAL_STORAGE_KEY);
    if (localStorageTalons) {
      const talons = JSON.parse(localStorageTalons);
      ctx.patchState({
        talons
      });
    }

    const accessToTalon = this.store.selectSnapshot(TalonAccessState.accessToTalons);
    if (accessToTalon.canReadTalon) {
      ctx.dispatch(new GetTalonTemplateList());
    }
  }

  @Action(GetTalonTemplateList)
  getTalonTemplateList(ctx: StateContext<TalonManageStateModel>): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.talonsService.getTalonTemplateList().subscribe({
      next: templateList => ctx.patchState({ templateList, loadTemplateListStatus: PROGRESS_STATUSES.SUCCESS }),
      error: err => ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL })
    });
  }

  @Action(CreateTalonTemplate)
  createTalonTemplate(ctx: StateContext<TalonManageStateModel>, { template, name }: CreateTalonTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.talonsService.createTemplate(name, template).subscribe({
      next: () => ctx.dispatch([new GetTalonTemplateList(), new UpdateTalonTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateTalonTemplateFail(err));
      }
    });
  }

  @Action(UpdateTalonTemplate)
  updateTalonTemplate(ctx: StateContext<TalonManageStateModel>, { template, id }: UpdateTalonTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.talonsService.updateTemplate(id, {
      html: template.html,
      project: template.project,
      formatId: template.formatId,
      css: template.css
    }, template.name).subscribe({
      next: () => ctx.dispatch([new GetTalonTemplateList(), new UpdateTalonTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateTalonTemplateFail(err));
      }
    });
  }

  @Action(DeleteTalonTemplate)
  deleteTalonTemplate(ctx: StateContext<TalonManageStateModel>, { id }: DeleteTalonTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.talonsService.deleteTemplate(id).subscribe({
      next: () => ctx.dispatch([new GetTalonTemplateList(), new UpdateTalonTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateTalonTemplateFail(err));
      }
    });
  }


  @Action(GetTalonById)
  getTalonById(ctx: StateContext<TalonManageStateModel>, { id }: GetTalonById): void {
    ctx.patchState({
      loadTalonStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    if (ctx.getState().talons?.[id]?.id) {
      this.talonsService.getTalonById(id).subscribe({
        next: talon => {
          if (talon) {
            ctx.dispatch(new UpdatedTalonByWS(talon));
          } else {
            ctx.patchState({
              loadTalonStatus: PROGRESS_STATUSES.SUCCESS
            });
          }
          ctx.patchState({
            currentTalonId: id
          });
        },
        error: err => ctx.dispatch(new GetTalonByIdFail(err))
      });
    } else {
      this.talonsService.getTalonById(id).subscribe({
        next: talon => ctx.dispatch(new GetTalonByIdSuccess(talon)),
        error: err => ctx.dispatch(new GetTalonByIdFail(err))
      });
    }
  }

  @Action(GetTalonByIdSuccess)
  getTalonByIdSuccess(ctx: StateContext<TalonManageStateModel>, { talon }: GetTalonByIdSuccess): void {
    // талона немає (видалений або чуже посилання) — бекенд віддає порожню відповідь замість 404
    if (!talon) {
      ctx.patchState({
        loadTalonStatus: PROGRESS_STATUSES.FAIL
      });
      return;
    }
    const talons = cloneDeep(ctx.getState().talons);
    talons[talon.id] = talon;
    ctx.patchState({
      talons,
      currentTalonId: talon.id,
      loadTalonStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(GetTalonByIdFail)
  getTalonByIdFail(ctx: StateContext<TalonManageStateModel>, { error }: GetTalonByIdFail): void {
    ctx.patchState({
      loadTalonStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(SetCurrentTalonId)
  setCurrentTalonId(ctx: StateContext<TalonManageStateModel>, { id }: SetCurrentTalonId): void {
    ctx.patchState({
      currentTalonId: id
    });
  }

  @Action(UpdateLocalTalon)
  updateLocalTalon(ctx: StateContext<TalonManageStateModel>, { talon }: UpdateLocalTalon): void {
    const talons = cloneDeep(ctx.getState().talons);
    talons[talon.id] = { ...talons[talon.id], ...talon };
    localStorage.setItem(TALONS_LOCAL_STORAGE_KEY, JSON.stringify(talons));
    ctx.patchState({
      talons
    });
  }

  @Action(DeleteLocalTalon)
  deleteLocalTalon(ctx: StateContext<TalonManageStateModel>, { id }: DeleteLocalTalon): void {
    const talons = cloneDeep(ctx.getState().talons);
    const realVersionTalons = cloneDeep(ctx.getState().realVersionTalons);

    delete talons[id];
    delete realVersionTalons[id];
    ctx.patchState({
      talons,
      realVersionTalons
    });
  }


  @Action(UpdateTalon)
  updateTalon(ctx: StateContext<TalonManageStateModel>, { id, talon, force }: UpdateTalon): void {
    ctx.patchState({
      updateTalonStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.talonsService.updateTalon(id, talon, force).subscribe({
      next: updatedTalon => ctx.dispatch(new UpdateTalonSuccess(updatedTalon)),
      error: err => ctx.dispatch(new UpdateTalonFail(err))
    });
  }

  @Action(UpdateTalonSuccess)
  updateTalonSuccess(ctx: StateContext<TalonManageStateModel>, { talon }: UpdateTalonSuccess): void {
    const talons = cloneDeep(ctx.getState().talons);
    ctx.patchState({
      updateTalonStatus: PROGRESS_STATUSES.SUCCESS,
      talons: { ...talons, [talon.id]: { ...talons[talon.id], version: talon.version, debtId: talon.debtId, debt: talon.debt } }
    });
  }

  @Action([UpdatedTalonByWS])
  updateTalonByWS(ctx: StateContext<TalonManageStateModel>, { talon }: UpdatedTalonByWS): void {
    const realVersionTalons = cloneDeep(ctx.getState().realVersionTalons);
    const talons = cloneDeep(ctx.getState().talons);
    const talonExist = talons[talon.id];

    if (talonExist) {
      ctx.patchState({
        loadTalonStatus: PROGRESS_STATUSES.SUCCESS,
        talons: {
          ...talons,
          [talon.id]: { ...talons[talon.id], updater: talon.updater, warranty: talon.warranty }
        }
      });
    }
    ctx.patchState({
      loadTalonStatus: PROGRESS_STATUSES.SUCCESS,
      realVersionTalons: { ...realVersionTalons, [talon.id]: talon }
    });
  }

  @Action(CreatedTalonByWS)
  createdTalonByWS(ctx: StateContext<TalonManageStateModel>, { talon }: CreatedTalonByWS): void {
    const talons = cloneDeep(ctx.getState().talons);
    ctx.patchState({
      talons: {
        ...talons,
        [talon.id]: {
          ...talons[talon.id],
          isChanged: false,
          isNew: false,
          updater: talon.updater,
          client: talon.client,
          equipment: talon.equipment
        }
      }
    });
  }


  @Action(CreateTalon)
  createTalon(ctx: StateContext<TalonManageStateModel>, { talon }: CreateTalon): void {
    ctx.patchState({
      updateTalonStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.talonsService.createTalon(talon).subscribe({
      next: createdTalon => ctx.dispatch(new CreateTalonSuccess(createdTalon)),
      error: err => ctx.dispatch(new CreateTalonFail(err))
    });
  }

  @Action(CreateTalonSuccess)
  createTalonSuccess(ctx: StateContext<TalonManageStateModel>, { talon }: CreateTalonSuccess): void {
    const talons = cloneDeep(ctx.getState().talons);
    ctx.patchState({
      updateTalonStatus: PROGRESS_STATUSES.SUCCESS,
      talons: { ...talons, [talon.id]: { ...talons[talon.id], debtId: talon.debtId, debt: talon.debt } }
    });
  }

  @Action(CreateTalonFail)
  createTalonFail(ctx: StateContext<TalonManageStateModel>, { error }: CreateTalonFail): void {
    ctx.patchState({
      updateTalonStatus: PROGRESS_STATUSES.FAIL
    });
  }
}


